import React, { useState } from "react";
import PlanShowcase from "@/components/PlanShowcase";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, BRAND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ShieldCheck, UserPlus, BadgeCheck, CalendarClock, Stethoscope, Scissors, FlaskConical, Pill, FileText, Cloud, MonitorSmartphone, Building2, ScanEye, Microscope, ArrowRight, Phone } from "lucide-react";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hospitalCode, setHospitalCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA step
  const [twoFa, setTwoFa] = useState(null);  // { pre_auth_token, user } or null
  const [twoFaCode, setTwoFaCode] = useState("");
  const [tab, setTab] = useState("signin");  // "signin" | "plans"

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      const res = await login(email.trim(), password, hospitalCode.trim());
      if (res?.requires_2fa) {
        setTwoFa({ pre_auth_token: res.pre_auth_token, user: res.user });
        setLoading(false);
        return;
      }
      if (res?.needs_branch_select || res?.needs_branch_pin) {
        navigate("/select-branch", { replace: true });
        return;
      }
      const from = location.state?.from?.pathname || "/app";
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e?.preventDefault?.();
    if (!twoFaCode.trim()) { setError("Enter a 6-digit code or recovery code."); return; }
    setError("");
    setLoading(true);
    try {
      const res = await verify2fa(twoFa.pre_auth_token, twoFaCode.trim());
      if (res?.needs_branch_select || res?.needs_branch_pin) {
        navigate("/select-branch", { replace: true });
        return;
      }
      const from = location.state?.from?.pathname || "/app";
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Invalid 2FA code.");
    } finally {
      setLoading(false);
    }
  };

  const cancel2fa = () => { setTwoFa(null); setTwoFaCode(""); setError(""); };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden bg-brand text-white md:flex md:min-h-screen md:flex-col md:p-8 lg:p-10">
        {/* Layered backgrounds */}
        <div className="iris-motif absolute inset-0 opacity-50" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.16),transparent_55%),radial-gradient(circle_at_85%_85%,rgba(56,189,248,0.18),transparent_55%)]" />

        {/* ===== TOP — Big logo + tagline ===== */}
        <div className="relative z-10 flex items-start justify-between">
          <img src={BRAND.logo} alt="" className="brand-logo-white h-28 w-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.4)] lg:h-36" data-testid="login-brand-logo" />
          <div className="pt-3 text-right">
            <div className="font-heading text-xs font-semibold uppercase tracking-widest text-white/90">Digital Care.</div>
            <div className="text-[10px] text-white/65">Better Vision. Stronger Outcomes.</div>
          </div>
        </div>

        {/* ===== HEADLINE ===== */}
        <div className="relative z-10 mt-8">
          <h1 className="font-heading text-3xl font-semibold leading-tight md:text-4xl lg:text-[2.4rem]">
            Smart digital workflow
            <br />
            for <span className="text-leaf-100">ophthalmology</span> hospitals &amp; clinics.
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80">
            Patient registration, OPD/Eye EMR, optometry, OT, lab, pharmacy, IPD,
            discharge, billing &amp; ABDM consent — in one fast, secure, multi-branch SaaS.
          </p>
        </div>

        {/* ===== 12-MODULE GRID ===== */}
        <div className="relative z-10 mt-6 rounded-2xl border border-white/15 bg-white/[0.06] p-3.5 shadow-2xl backdrop-blur-sm">
          <div className="mb-2.5 flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-white/85">12 modules · one platform</div>
            <div className="rounded-full bg-leaf-100/15 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-leaf-100">live</div>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {[
              { n: 1, label: "Patient Reg.", icon: UserPlus },
              { n: 2, label: "UHID", icon: BadgeCheck },
              { n: 3, label: "Appointments", icon: CalendarClock },
              { n: 4, label: "OPD EMR", icon: Stethoscope },
              { n: 5, label: "Optometry", icon: Eye },
              { n: 6, label: "OT / Surgery", icon: Scissors },
              { n: 7, label: "Lab", icon: FlaskConical },
              { n: 8, label: "Pharmacy", icon: Pill },
              { n: 9, label: "Billing", icon: ShieldCheck },
              { n: 10, label: "Discharge", icon: FileText },
              { n: 11, label: "IPD / Implants", icon: Building2 },
              { n: 12, label: "Reports", icon: FileText },
            ].map(({ n, label, icon: Icon }) => (
              <div key={n} className="group relative rounded-lg border border-white/10 bg-white/[0.05] p-1.5 text-center transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10" data-testid={`login-module-${n}`}>
                <div className="absolute -top-1.5 -left-1.5 grid h-4 w-4 place-items-center rounded-full bg-leaf-100 text-[8px] font-bold text-brand">{n}</div>
                <Icon size={14} className="mx-auto text-leaf-100" />
                <div className="mt-0.5 truncate text-[9px] font-medium leading-tight text-white/90">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== EYE-CARE PROCEDURE CARDS ===== */}
        <div className="relative z-10 mt-4 grid grid-cols-4 gap-2" data-testid="login-surgery-strip">
          {[
            { label: "Cataract Surgery", sub: "Phaco · IOL implant", icon: Eye, gradient: "from-sky-500/30 to-blue-600/10", emoji: "👁" },
            { label: "LASIK / Refractive", sub: "Laser vision care", icon: Scissors, gradient: "from-violet-500/30 to-indigo-600/10", emoji: "🔬" },
            { label: "Retina Imaging", sub: "OCT · Fundus scan", icon: ScanEye, gradient: "from-emerald-500/30 to-teal-600/10", emoji: "🩻" },
            { label: "Slit-Lamp Exam", sub: "Anterior segment", icon: Microscope, gradient: "from-amber-500/30 to-orange-600/10", emoji: "🔍" },
          ].map((p) => (
            <div key={p.label} className="group relative overflow-hidden rounded-xl border border-white/15 bg-gradient-to-br p-3 shadow-xl ring-1 ring-white/5 transition-all hover:-translate-y-0.5 hover:border-white/30">
              <div className={`absolute inset-0 bg-gradient-to-br ${p.gradient}`} />
              {/* Soft iris dot pattern accent */}
              <div className="absolute -right-3 -top-3 h-16 w-16 rounded-full bg-white/5 blur-xl" />
              <div className="absolute -bottom-4 -left-4 h-14 w-14 rounded-full bg-white/5 blur-lg" />

              <div className="relative z-10 flex flex-col h-full min-h-[110px] justify-between">
                <div className="flex items-start justify-between">
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-white/10 backdrop-blur-sm ring-1 ring-white/20">
                    <p.icon size={20} className="text-white" strokeWidth={1.5} />
                  </div>
                  <span className="text-xl opacity-50" aria-hidden>{p.emoji}</span>
                </div>
                <div>
                  <div className="font-heading text-[12.5px] font-semibold leading-tight text-white">{p.label}</div>
                  <div className="mt-0.5 text-[9px] uppercase tracking-wider text-white/60">{p.sub}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ===== FOOTER STRIP ===== */}
        <div className="relative z-10 mt-auto pt-6">
          <div className="flex items-center justify-between border-t border-white/10 pt-3 text-[10px] text-white/55">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-leaf-100" /> NABH</span>
              <span className="inline-flex items-center gap-1"><Cloud size={11} className="text-leaf-100" /> Cloud-enabled</span>
              <span className="inline-flex items-center gap-1"><MonitorSmartphone size={11} className="text-leaf-100" /> All devices</span>
            </div>
            <span className="rounded-full bg-leaf-100/15 px-2 py-0.5 font-medium uppercase tracking-wider text-leaf-100">Empowering Eye Care with Technology</span>
          </div>
          <div className="mt-1 text-[9.5px] text-white/45">{BRAND.footer}</div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col items-center justify-center bg-white px-6 py-10 md:px-14">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3 md:hidden">
            <img src={BRAND.icon} alt="" className="h-9 w-9" />
            <div className="leading-tight">
              <div className="font-heading text-lg font-semibold text-brand">{BRAND.name}</div>
              <div className="text-[10px] text-ink-muted">{BRAND.tagline}</div>
            </div>
          </div>
          <h2 className="font-heading text-3xl font-semibold">{twoFa ? "Two-factor verification" : (tab === "plans" ? "Our plans" : "Sign in")}</h2>
          <p className="mt-1 text-sm text-ink-muted">
            {twoFa
              ? <>Hi <b>{twoFa.user?.name || twoFa.user?.email}</b> — enter the 6-digit code from your authenticator app.</>
              : (tab === "plans" ? "Pick a plan → sign up → start using CareO." : "Welcome back. Enter your credentials to continue.")}
          </p>

          {/* Tab switcher — hidden during 2FA challenge */}
          {!twoFa && (
            <div className="mt-6 inline-flex rounded-md bg-slate-100 p-0.5 text-xs" data-testid="login-tabs">
              <button type="button" onClick={() => setTab("signin")} className={`rounded px-3 py-1.5 font-semibold ${tab === "signin" ? "bg-white text-brand shadow-sm" : "text-ink-muted"}`} data-testid="login-tab-signin">Sign in</button>
              <button type="button" onClick={() => setTab("plans")} className={`rounded px-3 py-1.5 font-semibold ${tab === "plans" ? "bg-white text-brand shadow-sm" : "text-ink-muted"}`} data-testid="login-tab-plans">Our plans</button>
            </div>
          )}

          {!twoFa && tab === "plans" ? (
            <div className="mt-6" data-testid="login-plans-pane">
              <PlanShowcase compact />
              <div className="mt-4 text-center text-[11px] text-ink-muted">
                Already a customer? <button type="button" onClick={() => setTab("signin")} className="font-semibold text-brand underline">Sign in</button>
              </div>
            </div>
          ) : null}

          {twoFa ? (
            <form onSubmit={submit2fa} className="mt-8 space-y-4" data-testid="login-2fa-form">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Authenticator code</Label>
                <Input
                  data-testid="login-2fa-code-input"
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value)}
                  placeholder="123456 or recovery code"
                  inputMode="text"
                  autoComplete="one-time-code"
                  autoFocus
                  className="mt-1 font-mono text-lg tracking-widest"
                />
                <div className="mt-1 text-[10px] text-ink-muted">Open Google / Microsoft Authenticator on your phone. Lost your phone? Enter one of the 8 recovery codes you saved during enrolment.</div>
              </div>
              {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>}
              <Button data-testid="login-2fa-submit-button" type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-700">
                {loading ? "Verifying…" : "Verify & continue"}
              </Button>
              <Button type="button" variant="outline" onClick={cancel2fa} className="w-full">Back to login</Button>
            </form>
          ) : (
          !twoFa && tab === "signin" ? (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="hospital-code" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Hospital code</Label>
              <Input
                id="hospital-code" name="hospital_code" data-testid="login-hospital-code-input"
                value={hospitalCode} onChange={(e) => setHospitalCode(e.target.value.toUpperCase())}
                placeholder="e.g. DEH" className="mt-1.5 uppercase"
                autoComplete="organization"
              />
              <div className="mt-1 text-[10px] text-ink-muted">Leave blank only if you are a super admin.</div>
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Email</Label>
              <Input
                id="email" data-testid="login-email-input" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@hospital.in" className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password" data-testid="login-password-input"
                  type={show ? "text" : "password"} required
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                />
                <button
                  type="button" onClick={() => setShow(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink"
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="mt-1 text-right">
                <a href="/forgot-password" className="text-[11px] font-medium text-brand hover:underline" data-testid="login-forgot-password">Forgot password?</a>
              </div>
            </div>
            {error && <div data-testid="login-error" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{error}</div>}
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-700">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          ) : null
          )}

          {!twoFa && (
            <div className="mt-5 flex items-center justify-center gap-2 rounded-lg border border-dashed border-brand/40 bg-brand/[0.04] px-4 py-3 text-[12px]">
              <span className="text-ink-muted">New to CareO?</span>
              <a
                href="/signup"
                data-testid="login-go-signup"
                className="inline-flex items-center gap-1 font-semibold text-brand hover:underline"
              >
                Start your free 30-day trial
                <ArrowRight size={12} />
              </a>
            </div>
          )}

          <div className="mt-8 text-center text-[11px] text-ink-muted">
            {/* Direct support / renewal helpline — surfaced on the login page
                so hospital staff or new prospects can reach us without hunting
                through legal footers. */}
            <a
              href="tel:+919616298162"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand-50 px-3 py-1 text-[11px] font-semibold text-brand hover:bg-brand-100"
              data-testid="login-support-phone"
            >
              <Phone size={12} />
              <span className="font-normal text-ink-muted">Any query / complaint / renewal:</span>
              <span className="font-mono">96162-98162</span>
            </a>
            <div className="mt-3 flex justify-center gap-4">
              <a href="/legal/terms" className="hover:text-brand" data-testid="login-footer-terms">Terms</a>
              <span>·</span>
              <a href="/legal/privacy" className="hover:text-brand" data-testid="login-footer-privacy">Privacy</a>
              <span>·</span>
              <a href="/legal/refund" className="hover:text-brand" data-testid="login-footer-refund">Refund</a>
              <span>·</span>
              <a href="/legal/dpa" className="hover:text-brand" data-testid="login-footer-dpa">DPA</a>
            </div>
            <div className="mt-2">{BRAND.footer}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
